import store from '@/store'
import Api from '@/services/api'
import { CompleteRegistrationDestinationType } from '@/common/enums'
import { ProspectModel } from '@/models/prospect'
import { getRegistrationRedirect, isNewRegistrationFlow } from '@/utils/registrationFlow'

const PENDING_ROUTE_KEY = 'pendingRouteAfterAuth'

const isAuthorized = (to, from, next) => {
  if (store.getters['auth/isAuthorised']) {
    next()
  } else {
    // Remember the target route so the user returns to it after sign-in (P5),
    // e.g. after tapping a push notification with an expired session.
    try {
      if (to.fullPath && to.fullPath !== '/sign-in') {
        localStorage.setItem(PENDING_ROUTE_KEY, to.fullPath)
      }
    } catch (e) {
      // ignore storage errors
    }
    next('/sign-in')
  }
}

const registrationRequired = (to, from, next) => {
  const redirect = getRegistrationRedirect(store.getters['user/user'])
  if (redirect && redirect.name !== to.name) {
    next(redirect)
  } else {
    next()
  }
}

const fetchPhoneVerificationStatus = async (studyId) => {
  try {
    return await Api.user.phoneVerificationStatus(studyId)
  } catch (e) {
    // The server refuses the study content until the phone is verified, so a failure here only skips the redirect
    return null
  }
}

const registrationNotRequired = async (to, from, next) => {
  const user = ProspectModel.parseFromApi({ prospect: store.getters['user/user'] })
  if (!user.requireRegistration) {
    // A registered participant who still has to verify their phone (#1709) stays on the details screen
    const status = await fetchPhoneVerificationStatus()
    if (status?.required) return next()
    const redirect = getRegistrationRedirect(user)
    next(redirect || { name: 'Dashboard' })
  } else {
    next()
  }
}

// Opening a study that requires phone verification (#1709) sends an unverified account to the details screen first
const phoneVerifiedForStudy = async (to, from, next) => {
  const status = await fetchPhoneVerificationStatus(to.params.studyId)
  if (status?.required) {
    store.commit('completeRegistration/SET_DESTINATION_STUDY', { studyId: to.params.studyId })
    next({ name: 'CompleteProfile' })
  } else {
    next()
  }
}

// The optional profile-details page (LL-169) is only reachable by a new-flow
// participant who has a selfie and has not dismissed it yet. Any other state is
// sent to the next outstanding checkpoint or the dashboard.
const registrationDetailsRequired = (to, from, next) => {
  const user = ProspectModel.parseFromApi({ prospect: store.getters['user/user'] })
  if (
    !user.requireRegistration &&
    isNewRegistrationFlow(user) &&
    user.image &&
    !user.registrationDetailsDismissed
  ) {
    next()
  } else {
    next(getRegistrationRedirect(user) || { name: 'Dashboard' })
  }
}

const destinationRedirectIfExists = (to, from, next) => {
  if (
    store.state?.completeRegistration?.destination === CompleteRegistrationDestinationType.STUDY &&
    store.state?.completeRegistration?.studyId
  ) {
    next(`/study/${store.state.completeRegistration.studyId}`)
  } else if (
    store.state?.completeRegistration?.destination === CompleteRegistrationDestinationType.CHAT &&
    store.state?.completeRegistration?.chatId
  ) {
    next(`/chats/${store.state.completeRegistration.chatId}`)
  } else {
    next()
  }
}

const test = (to, from, next) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log('resolve')
      resolve(next())
    }, 1000)
  })
}

export default {
  isAuthorized,
  destinationRedirectIfExists,
  registrationRequired,
  registrationNotRequired,
  phoneVerifiedForStudy,
  registrationDetailsRequired,
  test
}
